import { Form, Formik } from "formik";
import { useState } from "react";
import { FiArrowLeft, FiEye, FiEyeOff } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { portalChangePassword } from "@/features/portal/api";
import { portalHref } from "@/features/portal/portal-base";
import { ApiError } from "@/lib/api";
import {
  isStrongPortalPassword,
  PasswordStrengthChecklist,
} from "./password-strength";

const changePasswordSchema = Yup.object({
  old_password: Yup.string().required("Enter your current password"),
  password1: Yup.string()
    .required("Enter a new password")
    .test("strength", "Password does not meet the requirements", (v) =>
      isStrongPortalPassword(v ?? ""),
    ),
  password2: Yup.string()
    .required("Confirm your new password")
    .oneOf([Yup.ref("password1")], "Passwords do not match"),
});

interface ChangePasswordValues {
  old_password: string;
  password1: string;
  password2: string;
}

/** `/portal/password` — authenticated portal client changes their password. */
export function PortalChangePassword() {
  const navigate = useNavigate();
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);

  async function handleSubmit(
    values: ChangePasswordValues,
    { setFieldError }: { setFieldError: (f: string, m: string) => void },
  ) {
    try {
      await portalChangePassword(values);
      toast.success("Password updated");
      navigate(portalHref("/home"));
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setFieldError("old_password", "Your current password is incorrect");
        return;
      }
      toast.error("Couldn't update your password. Please try again.");
    }
  }

  return (
    <div className="mx-auto w-full max-w-md">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Change password</CardTitle>
        </CardHeader>
        <CardContent>
          <Formik<ChangePasswordValues>
            initialValues={{ old_password: "", password1: "", password2: "" }}
            validationSchema={changePasswordSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, values }) => (
              <Form className="grid gap-6">
                <FormField
                  name="old_password"
                  label="Current password"
                  type={showOld ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="h-11"
                  endAdornment={
                    <button
                      type="button"
                      onClick={() => setShowOld((v) => !v)}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      aria-label={showOld ? "Hide password" : "Show password"}
                    >
                      {showOld ? (
                        <FiEye className="size-4" />
                      ) : (
                        <FiEyeOff className="size-4" />
                      )}
                    </button>
                  }
                />

                <div className="grid gap-3">
                  <FormField
                    name="password1"
                    label="New password"
                    type={showNew ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="••••••••"
                    className="h-11"
                    endAdornment={
                      <button
                        type="button"
                        onClick={() => setShowNew((v) => !v)}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        aria-label={showNew ? "Hide password" : "Show password"}
                      >
                        {showNew ? (
                          <FiEye className="size-4" />
                        ) : (
                          <FiEyeOff className="size-4" />
                        )}
                      </button>
                    }
                  />
                  <PasswordStrengthChecklist value={values.password1} />
                </div>

                <FormField
                  name="password2"
                  label="Confirm new password"
                  type="password"
                  autoComplete="new-password"
                  placeholder="••••••••"
                  className="h-11"
                />

                <Button type="submit" disabled={isSubmitting} className="h-11">
                  {isSubmitting ? "Updating..." : "Update password"}
                </Button>
              </Form>
            )}
          </Formik>

          <Link
            to={portalHref("/home")}
            className="text-muted-foreground hover:text-foreground mt-6 flex items-center justify-center gap-2 text-sm transition-colors"
          >
            <FiArrowLeft className="size-4" />
            Back to portal
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
